import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, GraduationCap } from "lucide-react";
import { motion } from "framer-motion";
import teacherHero from "../assets/images/teacher-classes.jpeg";
import { apiClient } from "../lib/apiClient";

type ClassSummary = {
  id: number;
  code: string;
  title: string;
  term: string;
  students: number;
};

interface ApiCourse {
  id: string;
  code: string;
  title: string;
  startDate: string | null;
  endDate: string | null;
}

function termLabel(start: string | null): string {
  if (!start) return "Active";
  const d = new Date(start);
  const month = d.getMonth();
  const year = d.getFullYear();
  if (month <= 5) return `Spring ${year}`;
  if (month <= 7) return `Summer ${year}`;
  return `Autumn ${year}`;
}

export default function ClassesPage() {
  const navigate = useNavigate();
  const [classes, setClasses] = useState<ClassSummary[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient.functions
      .invoke<{ courses: ApiCourse[] }>("educator_settings", {
        body: { action: "load" },
      })
      .then(({ data, error }) => {
        if (!error && data?.courses) {
          setClasses(
            data.courses.map((c, i) => ({
              id: i + 1,
              code: c.code,
              title: c.title,
              term: termLabel(c.startDate),
              students: 0,
            })),
          );
        }
      })
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-10">
      {/* Hero banner */}
      <section
        className="relative overflow-hidden rounded-2xl bg-gray-900 text-white shadow-sm"
        style={{ backgroundImage: `url(${teacherHero})`, backgroundSize: "cover" }}
      >
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

        <div className="relative z-10 flex flex-col gap-6 p-8 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-semibold">Your Courses</h1>
            <p className="mt-2 max-w-md text-sm text-gray-200">
              Overview of all active &amp; upcoming courses. Click a card to drill
              in.
            </p>
            <button
              className="mt-6 inline-flex items-center gap-2 rounded-md bg-white/10 px-4 py-2 text-sm font-medium backdrop-blur transition hover:bg-white/20"
              onClick={() => navigate("/app/courses/new")}
            >
              <Plus size={16} /> New course
            </button>
          </div>

          {/* Quick stat */}
          <div className="flex items-center gap-3 rounded-lg bg-white/10 px-4 py-3 text-sm font-medium backdrop-blur">
            <GraduationCap size={18} />{" "}
            {loading ? "…" : `${classes.length} total courses`}
          </div>
        </div>

        <p className="absolute bottom-2 right-4 text-[10px] text-gray-300">
          Photo by{" "}
          <a
            href="https://www.pexels.com/@haidar-azmi-2148111388"
            target="_blank"
            rel="noreferrer"
            className="underline"
          >
            Haidar Azmi
          </a>{" "}
          on{" "}
          <a href="https://www.pexels.com" target="_blank" rel="noreferrer" className="underline">
            Pexels
          </a>
        </p>
      </section>

      {/* Cards */}
      {loading ? (
        <p className="text-center text-sm text-gray-500">Loading courses…</p>
      ) : classes.length === 0 ? (
        <p className="text-center text-sm text-gray-500">
          No courses yet. Add courses in{" "}
          <button
            className="text-primary underline"
            onClick={() => navigate("/app/settings")}
          >
            Settings
          </button>
          .
        </p>
      ) : (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {classes.map((c) => (
            <motion.button
              key={c.id}
              onClick={() => navigate(`/app/courses/${c.id}`)}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 0.4 }}
              className="flex flex-col rounded-xl bg-white p-5 text-left shadow-sm transition-all hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary/60"
            >
              <div className="flex-1">
                <p className="text-sm font-medium text-primary">{c.code}</p>
                <h2 className="mt-1 line-clamp-2 text-lg font-semibold text-gray-800">
                  {c.title}
                </h2>
                <p className="mt-2 text-sm text-gray-600">{c.term}</p>
              </div>
              <div className="mt-4 flex items-center justify-between text-sm">
                <span className="text-gray-500">Students</span>
                <span className="font-medium text-gray-800">{c.students}</span>
              </div>
            </motion.button>
          ))}
        </div>
      )}
    </div>
  );
}
